import posthog, { type PostHog, type PostHogConfig, type Properties } from 'posthog-js';
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createExceptionReporter,
  type ExceptionReporter,
  type ExceptionReportingAdapter,
  type SafeExceptionContext,
} from './exception-reporting';

export interface PostHogInitOptions {
  apiKey?: string;
  apiHost?: string;
  appName?: string;
  appVersion?: string;
  environment?: string;
  autocapture?: boolean;
  capturePageview?: boolean;
  disableSessionRecording?: boolean;
  persistence?: 'localStorage' | 'sessionStorage' | 'cookie' | 'memory';
  config?: Partial<PostHogConfig>;
}

let initialized = false;

/**
 * Initialize PostHog for web applications and browser extensions.
 * Safe to call multiple times; subsequent calls are no-ops.
 */
export function initPostHog(options: PostHogInitOptions = {}): PostHog | null {
  if (typeof window === 'undefined') return null;
  if (initialized) return posthog;

  const importMetaEnv =
    typeof import.meta !== 'undefined' && 'env' in import.meta
      ? (import.meta as ImportMeta & { env?: Record<string, string> }).env
      : undefined;

  const apiKey =
    options.apiKey ||
    (typeof process !== 'undefined' && process.env?.VITE_PUBLIC_POSTHOG_KEY) ||
    importMetaEnv?.VITE_PUBLIC_POSTHOG_KEY;

  const apiHost =
    options.apiHost ||
    (typeof process !== 'undefined' && process.env?.VITE_PUBLIC_POSTHOG_HOST) ||
    importMetaEnv?.VITE_PUBLIC_POSTHOG_HOST ||
    'https://us.i.posthog.com';

  if (!apiKey) {
    return null;
  }

  try {
    const environment =
      options.environment ||
      importMetaEnv?.MODE ||
      (typeof process !== 'undefined' && (process.env?.NODE_ENV || process.env?.ENVIRONMENT)) ||
      'development';

    const appName =
      options.appName ||
      (typeof process !== 'undefined' && process.env?.VITE_PUBLIC_APP_NAME) ||
      importMetaEnv?.VITE_PUBLIC_APP_NAME;

    const appVersion =
      options.appVersion ||
      (typeof process !== 'undefined' &&
        (process.env?.VITE_PUBLIC_APP_VERSION || process.env?.GIT_SHA)) ||
      importMetaEnv?.VITE_PUBLIC_APP_VERSION ||
      importMetaEnv?.VITE_PUBLIC_GIT_SHA;

    posthog.init(apiKey, {
      api_host: apiHost,
      autocapture: options.autocapture ?? true,
      capture_pageview: options.capturePageview ?? true,
      disable_session_recording: options.disableSessionRecording ?? false,
      capture_exceptions: false,
      persistence: options.persistence ?? 'localStorage',
      loaded: (ph) => {
        ph.register({
          environment,
          ...(appName ? { app_name: appName } : {}),
          ...(appVersion ? { app_version: appVersion } : {}),
        });
      },
      ...options.config,
    });
    initialized = true;
    return posthog;
  } catch (err) {
    console.error('[PostHog] Initialization error:', err);
    return null;
  }
}

/**
 * Returns the active PostHog instance if initialized.
 */
export function getPostHogClient(): PostHog | null {
  if (typeof window === 'undefined' || !initialized) return null;
  return posthog;
}

/**
 * Shared PostHog JS web/extension adapter adhering to ExceptionReportingAdapter contract.
 */
export const posthogAdapter: ExceptionReportingAdapter = {
  captureException(error: Error, context: SafeExceptionContext) {
    const client = getPostHogClient();
    if (!client) return;
    client.captureException(error, context as Properties);
  },
};

/**
 * Pre-configured web exception reporter composing the PostHog adapter.
 */
export const webExceptionReporter: ExceptionReporter = createExceptionReporter(posthogAdapter);

/**
 * Captures a custom event with properties.
 */
export function captureEvent(eventName: string, properties?: Record<string, unknown>): void {
  const client = getPostHogClient();
  if (client) {
    client.capture(eventName, properties);
  }
}

/**
 * Captures an exception/error through the shared contract and PostHog JS captureException.
 */
export function captureException(error: unknown, context?: SafeExceptionContext): Promise<void> {
  return webExceptionReporter.report(error, context);
}

/**
 * Identifies a user with their distinct ID and properties.
 */
export function identifyUser(distinctId: string, properties?: Record<string, unknown>): void {
  const client = getPostHogClient();
  if (client) {
    client.identify(distinctId, properties);
  }
}

/**
 * Resets user identification on sign out.
 */
export function resetUser(): void {
  const client = getPostHogClient();
  if (client) {
    client.reset();
  }
}

/**
 * Listens for uncaught errors and unhandled promise rejections on window, reporting through the shared contract.
 */
export function setupGlobalErrorTracking(): () => void {
  if (typeof window === 'undefined') return () => {};

  const errorHandler = (event: ErrorEvent) => {
    captureException(event.error ?? event.message);
  };

  const rejectionHandler = (event: PromiseRejectionEvent) => {
    captureException(event.reason ?? 'Unhandled Promise Rejection');
  };

  window.addEventListener('error', errorHandler);
  window.addEventListener('unhandledrejection', rejectionHandler);

  return () => {
    window.removeEventListener('error', errorHandler);
    window.removeEventListener('unhandledrejection', rejectionHandler);
  };
}

const PostHogReactContext = createContext<PostHog | null>(null);

export interface PostHogProviderProps {
  children: React.ReactNode;
  client?: PostHog | null;
  initOptions?: PostHogInitOptions;
}

export function PostHogProvider({
  children,
  client,
  initOptions,
}: PostHogProviderProps): React.JSX.Element {
  const [activeClient, setActiveClient] = useState<PostHog | null>(() => {
    if (client !== undefined) return client;
    return initPostHog(initOptions);
  });

  useEffect(() => {
    if (client === undefined && !activeClient) {
      const initializedClient = initPostHog(initOptions);
      if (initializedClient) {
        setActiveClient(initializedClient);
      }
    }
  }, [client, initOptions, activeClient]);

  useEffect(() => {
    const cleanup = setupGlobalErrorTracking();
    return cleanup;
  }, []);

  return React.createElement(PostHogReactContext.Provider, { value: activeClient }, children);
}

export function usePostHog(): PostHog | null {
  return useContext(PostHogReactContext) ?? getPostHogClient();
}
